import { useIDEState } from '@/hooks/use-ide-state';
import { portfolioStorage } from '@/utils/storage';
import React, { useEffect, useRef } from 'react';

export const URLRouter: React.FC = () => {
    const { state, navigateToFile, navigateToProject, updateState } = useIDEState();
    const hasHandledInitialNavigation = useRef(false);
    const isFirstVisit = useRef(portfolioStorage.getIDEState() === null);

    useEffect(() => {
        const clearSelection = () => {
            updateState({
                tabs: state.tabs.map(tab => ({ ...tab, isActive: false })),
                activeTabId: null,
                selectedFile: null,
                selectedProject: null,
            });
        };

        const handleURLNavigation = (isInitial = false) => {
            let pathSegments: string[];
            try {
                pathSegments = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent);
            } catch {
                window.history.replaceState({}, '', '/');
                clearSelection();
                return;
            }

            if (pathSegments.length === 0) {
                // Preserve restored tabs on initial load; history navigation to root clears focus.
                if (!isInitial) clearSelection();
                else if (isFirstVisit.current) {
                    navigateToFile('Portfolio', 'index.html', 'none');
                    window.history.replaceState({}, '', '/Portfolio/index.html');
                }
                return;
            }

            const [projectName, fileName] = pathSegments;
            const project = state.projects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
            const file = project?.files.find(f => f.name.toLowerCase() === fileName?.toLowerCase());

            if (!project || pathSegments.length > 2 || (pathSegments.length === 2 && !file)) {
                window.history.replaceState({}, '', '/');
                clearSelection();
                return;
            }

            if (pathSegments.length === 1) {
                navigateToProject(projectName, 'none');
            } else {
                navigateToFile(projectName, fileName, 'none');
            }
        };

        if (!hasHandledInitialNavigation.current) {
            hasHandledInitialNavigation.current = true;
            handleURLNavigation(true);
        }

        const handlePopState = () => handleURLNavigation();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [state.projects, state.tabs, navigateToFile, navigateToProject, updateState]);

    return null;
};
